import { queryOptions } from '@tanstack/react-query'

import { api } from '@/lib/api'

import { successStaleTime } from '@/shared/api/utils/query'

import type { Tenant, TenantDetails } from './types'

/**
 * Session-scoped Tenant reads — *which tenant am I in, and what is it entitled to.*
 * The Tenant *management* surface (users, invitations, subtenants) is `features/tenant`.
 *
 * This module must not import any other `shared/auth` module: `getters.ts` reads the
 * details key straight out of the cache and `store.tsx` re-exports `getters`, so an
 * edge back into auth from here recreates the auth↔tenant import cycle this split
 * exists to remove. `useTenantDetails` lives in `hooks/use-derived-auth.ts` for that
 * reason.
 */
export const tenantSessionKeys = {
	all: ['tenant-session'] as const,
	details: (tenant: string) =>
		[...tenantSessionKeys.all, 'details', tenant] as const,
}

export const fetchTenantDetails = async (
	tenant: string,
	signal?: AbortSignal,
): Promise<TenantDetails> => {
	const { data } = await api.get<TenantDetails>(`/api/tenants/${tenant}`, {
		signal,
	})

	return data
}

export const tenantSessionQueries = {
	details: (tenant: string) =>
		queryOptions({
			queryKey: tenantSessionKeys.details(tenant),
			queryFn: ({ signal }) => fetchTenantDetails(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'h'),
		}),
}

/**
 * Get the tenant name by ID
 * @param tenantID - The tenant ID
 * @param allowed_tenants - The list of tenants
 * @returns The tenant fullname or undefined
 */
export const getTenantNameById = (
	tenantID: string | undefined,
	allowed_tenants: Array<Tenant>,
) => {
	const tenant = allowed_tenants.find((t) => t.id === tenantID)
	return tenant?.fullname
}

/**
 * Build a Map of tenant ID to tenant display name
 * @param tenants - array of tenant objects from allowed_tenants
 */
export const buildTenantMap = (tenants: Array<Tenant>) =>
	new Map(tenants.map((t) => [t.id, t.fullname]))
